import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "ecran-bleu-bsod",
  silo: "windows",
  h1: "Écran bleu Windows (BSOD) : pourquoi ça arrive et comment le résoudre ?",
  metaTitle: "Écran bleu Windows BSOD : causes et solutions",
  metaDescription: "L'écran bleu de la mort fait peur mais il a toujours une cause. Découvrez comment lire le code d'erreur et réparer votre Windows.",
  excerpt: "L'écran bleu (BSOD) est le signal d'alarme de Windows quand il détecte une erreur critique. Il est toujours accompagné d'un code d'erreur qui permet de comprendre ce qui s'est passé -- et de trouver la solution.",
  publishedAt: "2024-01-10",
  readingMinutes: 6,
  relatedSlugs: ["ordinateur-ne-demarre-plus", "mise-a-jour-windows-bloquee", "erreurs-windows-frequentes", "pc-bloque-demarrage-windows"],
  faq: [
    {
      question: "L'écran bleu est-il dangereux pour mes données ?",
      answer: "Un écran bleu isolé ne détruit pas vos données. En revanche, des BSOD répétés peuvent signaler un disque dur en train de tomber en panne -- ce qui peut mener à une perte de données si on n'agit pas rapidement.",
    },
    {
      question: "Comment trouver la cause d'un écran bleu passé ?",
      answer: "Windows garde un journal des erreurs. Dans le menu Démarrer, cherchez 'Observateur d'événements', puis Windows Logs > System. Cherchez les entrées de type 'Critique' ou 'Erreur' aux heures des BSOD.",
    },
    {
      question: "Mon PC redémarre trop vite et je n'ai pas le temps de lire le code d'erreur ?",
      answer: "Allez dans Panneau de configuration > Système > Paramètres système avancés > Démarrage et récupération. Décochez 'Redémarrer automatiquement'. Le prochain BSOD affichera l'écran sans redémarrer.",
    },
  ],
  content: [
    {
      type: "p",
      text: "L'écran bleu de la mort (Blue Screen of Death, ou BSOD) est redouté, mais il a toujours une raison d'être. Windows l'affiche quand il détecte une erreur si grave qu'il ne peut pas continuer à fonctionner sans risquer de corrompre vos données. C'est en réalité un mécanisme de protection.",
    },
    {
      type: "h2",
      text: "Comment lire un écran bleu",
    },
    {
      type: "p",
      text: "Sur Windows 10 et 11, l'écran bleu affiche un emoji triste et un message court, suivi d'un code comme CRITICAL_PROCESS_DIED ou MEMORY_MANAGEMENT. Notez ce code -- il est la clé pour comprendre la cause.",
    },
    {
      type: "h2",
      text: "Les codes d'erreur BSOD les plus courants",
    },
    {
      type: "ul",
      items: [
        "CRITICAL_PROCESS_DIED : un processus Windows essentiel a planté -- souvent dû à une mise à jour ou un pilote",
        "MEMORY_MANAGEMENT : problème avec la RAM -- elle peut être défaillante ou mal configurée",
        "SYSTEM_SERVICE_EXCEPTION : pilote (driver) incompatible ou corrompu",
        "NTFS_FILE_SYSTEM : problème avec le disque dur ou le système de fichiers Windows",
        "DRIVER_IRQL_NOT_LESS_OR_EQUAL : pilote matériel défectueux (souvent la carte réseau ou graphique)",
        "WHEA_UNCORRECTABLE_ERROR : erreur matérielle -- processeur, RAM ou carte mère",
      ],
    },
    {
      type: "h2",
      text: "Causes principales des BSOD",
    },
    {
      type: "h3",
      text: "Pilotes défectueux ou incompatibles",
    },
    {
      type: "p",
      text: "C'est la cause la plus fréquente. Après l'installation d'un nouveau matériel ou d'une [mise à jour Windows](slug:mise-a-jour-windows-bloquee), un pilote peut entrer en conflit avec le système et provoquer des BSOD.",
    },
    {
      type: "h3",
      text: "RAM défaillante",
    },
    {
      type: "p",
      text: "Un barrette de RAM avec des cellules défectueuses provoque des erreurs aléatoires difficiles à diagnostiquer. L'outil Windows Memory Diagnostic (cherchez-le dans le menu Démarrer) peut tester votre RAM.",
    },
    {
      type: "h3",
      text: "Disque dur en train de tomber en panne",
    },
    {
      type: "p",
      text: "Un disque dur vieillissant avec des secteurs défectueux génère des BSOD de type NTFS_FILE_SYSTEM. C'est urgent à traiter avant que le disque ne lâche complètement.",
    },
    {
      type: "h3",
      text: "Surchauffe",
    },
    {
      type: "p",
      text: "Un processeur ou une carte graphique qui [surchauffe](slug:pc-qui-chauffe) peut provoquer des BSOD pour se protéger. Vérifiez les températures avec HWMonitor.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "BSOD répétés : agissez vite",
      text: "Un BSOD isolé après une mise à jour est souvent bénin. Mais si les BSOD se répètent plusieurs fois par jour, c'est un signe d'alerte sérieux. Ne tardez pas -- votre disque dur ou votre RAM est peut-être en train de lâcher.",
    },
    {
      type: "h2",
      text: "Comment résoudre un BSOD",
    },
    {
      type: "ol",
      items: [
        "Notez le code d'erreur et cherchez-le sur support.microsoft.com",
        "Si le BSOD est apparu après une mise à jour, désinstallez-la via Paramètres > Windows Update > Historique des mises à jour > Désinstaller",
        "Lancez l'outil Vérification du disque : ouvrez une invite de commandes en admin et tapez 'chkdsk C: /f /r'",
        "Testez la RAM avec Windows Memory Diagnostic",
        "Mettez à jour tous les pilotes, en particulier carte graphique et chipset",
        "Si le problème persiste, une réinstallation de Windows peut être nécessaire",
      ],
    },
    {
      type: "h2",
      text: "Si votre PC ne démarre plus après un BSOD",
    },
    {
      type: "p",
      text: "Un BSOD peut parfois empêcher Windows de démarrer normalement. Consultez notre guide sur l'[ordinateur qui ne démarre plus](slug:ordinateur-ne-demarre-plus) pour les étapes à suivre. Nos techniciens peuvent se déplacer et analyser les journaux de crash pour identifier précisément la cause.",
    },
  ],
};

export default article;
